import { getOptimizationEventConfig } from "@lib/contentOptimization/getOptimizationEventConfig";
import sharedConstants from "@lib/shared_constants";

import type { OptimizationEventType } from "@canvas/Steps/ContentOptimizationStep/ContentOptimizationStep.types";
import {
  fromConversionBehavior,
  toConversionBehavior,
} from "@canvas/Steps/ContentOptimizationStep/ContentOptimizationStep.types";

import { getChannelForMessageType } from "./getChannelForMessageType";
import type { ContentOptimizationChannel } from "./types";

/**
 * Resolves a backend analytics stat key (e.g. "opens", "clicks") and messaging
 * actions into the corresponding UI OptimizationEventType.
 *
 * This centralizes the channel/protocol awareness so that consuming components
 * never need channel-specific branching to derive display labels.
 */
export function resolveOptimizationEventType(
  conversionType: string | undefined,
  messageTypes: string[],
  messagingActions: Array<{ message_type?: string; protocol?: string }> | undefined
): OptimizationEventType | undefined {
  const normalizedType = conversionType?.toLowerCase() as OptimizationEventType | undefined;
  if (!normalizedType) {
    return undefined;
  }

  const behavior = toConversionBehavior(normalizedType);
  if (!behavior) {
    return undefined;
  }

  const channel = messageTypes
    .map(getChannelForMessageType)
    .find((channel): channel is ContentOptimizationChannel => channel !== undefined);
  if (!channel) {
    return undefined;
  }

  const smsProtocol = channel === "sms" ? getSmsProtocol(messagingActions) : null;
  const config = getOptimizationEventConfig(channel, smsProtocol);

  return fromConversionBehavior(behavior, config);
}

function getSmsProtocol(
  messagingActions: Array<{ message_type?: string; protocol?: string }> | undefined
): string | null {
  if (!messagingActions) {
    return null;
  }
  const smsAction = messagingActions.find(
    (action) => action.message_type === sharedConstants.CAMPAIGN_MESSAGE_TYPES.SMS
  );
  return smsAction?.protocol ?? null;
}
